import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { api, toApiMessage } from "../api/client"
import type { Paged, Session } from "../api/types"
import { useAuth } from "../auth/AuthContext"
import { Icon } from "../components/Icon"
import {
  Badge,
  Button,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  PageHeader,
  Segmented,
  showToast
} from "../components/ui"
import { useFamily } from "../family/FamilyContext"
import { formatDate, formatRelative, initials, ROLE_LABELS } from "../lib/format"
import { cleanupPushSubscription, currentPushSubscription, supportsWebPush } from "../pwa/push"
import { normalizeSecondFactor, rawSecondFactor } from "../lib/input"
import { copyText } from "../lib/clipboard"

type ThemePreference = "system" | "light" | "dark"

interface InstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>
}

function applyTheme(preference: ThemePreference) {
  const dark = preference === "dark" || (preference === "system" && matchMedia("(prefers-color-scheme: dark)").matches)
  document.documentElement.dataset.theme = dark ? "dark" : "light"
  document.documentElement.style.colorScheme = dark ? "dark" : "light"
}

function base64ToBytes(value: string) {
  const padding = "=".repeat((4 - (value.length % 4)) % 4)
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/")
  const raw = atob(base64)
  const bytes = Uint8Array.from(Array.from(raw), (character) => character.charCodeAt(0))
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer
}

function TwoFactorPanel() {
  const { user, updateUser } = useAuth()
  const [setup, setSetup] = useState<{ secret: string; otpauthUri: string } | null>(null)
  const [code, setCode] = useState("")
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null)
  const [disableOpen, setDisableOpen] = useState(false)
  const [disableValues, setDisableValues] = useState({ password: "", secondFactor: "" })
  const setupMutation = useMutation({
    mutationFn: () => api.post<{ secret: string; otpauthUri: string }>("/auth/2fa/setup"),
    onSuccess: setSetup
  })
  const confirmMutation = useMutation({
    mutationFn: () => api.post<{ recoveryCodes: string[] }>("/auth/2fa/confirm", { code: rawSecondFactor(code) }),
    onSuccess: (result) => {
      setRecoveryCodes(result.recoveryCodes)
      setSetup(null)
      if (user) updateUser({ ...user, twoFactorEnabled: true, requiresTwoFactorSetup: false })
      showToast("Двухфакторная защита включена")
    }
  })
  const disableMutation = useMutation({
    mutationFn: () =>
      api.post<void>("/auth/2fa/disable", {
        ...disableValues,
        secondFactor: rawSecondFactor(disableValues.secondFactor)
      }),
    onSuccess: () => {
      setDisableOpen(false)
      if (user) updateUser({ ...user, twoFactorEnabled: false })
      showToast("Двухфакторная защита отключена", "neutral")
    }
  })

  if (recoveryCodes)
    return (
      <div className="settings-card settings-card--wide recovery-card">
        <div className="settings-card__icon is-success">
          <Icon name="shield" />
        </div>
        <div className="settings-card__content">
          <div className="settings-card__title">
            <div>
              <h2>Сохраните recovery-коды</h2>
              <p>Каждый код работает один раз. После закрытия мы их больше не покажем.</p>
            </div>
            <Badge tone="success">2FA включена</Badge>
          </div>
          <div className="recovery-codes">
            {recoveryCodes.map((item) => (
              <code key={item}>{item}</code>
            ))}
          </div>
          <div className="button-row">
            <Button
              variant="secondary"
              icon="copy"
              onClick={() =>
                void copyText(recoveryCodes.join("\n")).then((copied) =>
                  showToast(
                    copied ? "Коды скопированы" : "Выделите и скопируйте коды вручную",
                    copied ? "success" : "neutral"
                  )
                )
              }
            >
              Скопировать
            </Button>
            <Button onClick={() => setRecoveryCodes(null)}>Я сохранил коды</Button>
          </div>
        </div>
      </div>
    )

  return (
    <div className="settings-card settings-card--wide">
      <div className={`settings-card__icon ${user?.twoFactorEnabled ? "is-success" : "is-warning"}`}>
        <Icon name="shield" />
      </div>
      <div className="settings-card__content">
        <div className="settings-card__title">
          <div>
            <h2>Двухфакторная защита</h2>
            <p>Одноразовый код из любого TOTP-приложения. Внешний аккаунт не требуется.</p>
          </div>
          <Badge tone={user?.twoFactorEnabled ? "success" : "warning"}>
            {user?.twoFactorEnabled ? "включена" : "не включена"}
          </Badge>
        </div>
        {setup ? (
          <form
            className="two-factor-setup"
            onSubmit={(event) => {
              event.preventDefault()
              confirmMutation.mutate()
            }}
          >
            <div className="secret-box">
              <span>Секрет для приложения</span>
              <code>{setup.secret}</code>
              <button
                type="button"
                onClick={() =>
                  void copyText(setup.secret).then((copied) =>
                    showToast(
                      copied ? "Секрет скопирован" : "Выделите секрет и скопируйте вручную",
                      copied ? "success" : "neutral"
                    )
                  )
                }
              >
                <Icon name="copy" size={17} />
              </button>
            </div>
            <a href={setup.otpauthUri} className="text-button">
              <Icon name="external" size={16} /> Открыть в приложении-аутентификаторе
            </a>
            <div className="inline-confirm">
              <Field label="Проверочный код">
                <input
                  className="otp-input"
                  required
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(normalizeSecondFactor(e.target.value))}
                  placeholder="000 000"
                />
              </Field>
              <Button type="submit" loading={confirmMutation.isPending}>
                Подтвердить
              </Button>
            </div>
            {confirmMutation.isError ? <div className="form-error">{toApiMessage(confirmMutation.error)}</div> : null}
          </form>
        ) : user?.twoFactorEnabled ? (
          <Button variant="secondary" onClick={() => setDisableOpen(true)}>
            Отключить 2FA
          </Button>
        ) : (
          <Button icon="key" loading={setupMutation.isPending} onClick={() => setupMutation.mutate()}>
            Настроить 2FA
          </Button>
        )}
        {setupMutation.isError ? <div className="form-error">{toApiMessage(setupMutation.error)}</div> : null}
      </div>
      <Dialog
        open={disableOpen}
        onClose={() => setDisableOpen(false)}
        title="Отключить 2FA"
        eyebrow="Подтвердите действие"
      >
        <form
          className="form-stack"
          onSubmit={(event) => {
            event.preventDefault()
            disableMutation.mutate()
          }}
        >
          <Field label="Текущий пароль">
            <input
              required
              type="password"
              autoComplete="current-password"
              value={disableValues.password}
              onChange={(e) => setDisableValues({ ...disableValues, password: e.target.value })}
            />
          </Field>
          <Field label="Код 2FA или recovery-код">
            <input
              required
              inputMode="text"
              autoCapitalize="characters"
              autoComplete="one-time-code"
              value={disableValues.secondFactor}
              onChange={(e) =>
                setDisableValues({ ...disableValues, secondFactor: normalizeSecondFactor(e.target.value) })
              }
            />
          </Field>
          {disableMutation.isError ? <div className="form-error">{toApiMessage(disableMutation.error)}</div> : null}
          <div className="dialog__actions">
            <Button type="button" variant="ghost" onClick={() => setDisableOpen(false)}>
              Отмена
            </Button>
            <Button type="submit" variant="danger" loading={disableMutation.isPending}>
              Отключить
            </Button>
          </div>
        </form>
      </Dialog>
    </div>
  )
}

function PushPanel() {
  const [state, setState] = useState<"unsupported" | "default" | "denied" | "enabled">(() =>
    !supportsWebPush() ? "unsupported" : Notification.permission === "granted" ? "default" : Notification.permission
  )
  const [pending, setPending] = useState(false)
  const [error, setError] = useState("")

  useEffect(() => {
    if (state !== "default" || Notification.permission !== "granted") return
    void currentPushSubscription().then((subscription) => {
      if (subscription) setState("enabled")
    })
  }, [state])

  const enable = async () => {
    setPending(true)
    setError("")
    try {
      const permission = await Notification.requestPermission()
      if (permission !== "granted") {
        setState(permission)
        return
      }
      const { enabled, publicKey } = await api.get<{ enabled: boolean; publicKey: string | null }>("/push/public-key")
      if (!enabled || !publicKey) throw new Error("Push не настроен администратором сервера")
      const registration = await navigator.serviceWorker.getRegistration()
      if (!registration) throw new Error("Установите приложение или перезагрузите страницу, чтобы включить push")
      const existing = await registration.pushManager.getSubscription()
      const subscription =
        existing ??
        (await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: base64ToBytes(publicKey)
        }))
      const serialized = subscription.toJSON()
      await api.post("/push/subscriptions", {
        endpoint: subscription.endpoint,
        keys: { p256dh: serialized.keys?.p256dh, auth: serialized.keys?.auth }
      })
      setState("enabled")
      showToast("Push-уведомления включены")
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : toApiMessage(reason, "Не удалось включить push"))
    } finally {
      setPending(false)
    }
  }
  const disable = async () => {
    setPending(true)
    setError("")
    try {
      await cleanupPushSubscription()
      setState("default")
      showToast("Push-уведомления на этом устройстве отключены", "neutral")
    } catch (reason) {
      setError(toApiMessage(reason, "Не удалось отключить push"))
    } finally {
      setPending(false)
    }
  }
  return (
    <div className="settings-card">
      <div className={`settings-card__icon ${state === "enabled" ? "is-success" : ""}`}>
        <Icon name="bell" />
      </div>
      <div className="settings-card__content">
        <div className="settings-card__title">
          <div>
            <h2>Push-уведомления</h2>
            <p>Для предметов, наград и важных действий.</p>
          </div>
          {state === "enabled" ? <Badge tone="success">включены</Badge> : null}
        </div>
        {state === "unsupported" ? (
          <p className="muted">Этот браузер не поддерживает Web Push.</p>
        ) : state === "denied" ? (
          <div className="info-callout info-callout--warning">
            <p>Уведомления запрещены в настройках браузера. Разрешите их для этого сайта.</p>
          </div>
        ) : state === "enabled" ? (
          <div>
            <p className="settings-success">
              <Icon name="check" size={17} /> Это устройство будет получать уведомления.
            </p>
            <Button variant="ghost" loading={pending} onClick={() => void disable()}>
              Отключить на устройстве
            </Button>
          </div>
        ) : (
          <Button variant="secondary" loading={pending} onClick={() => void enable()}>
            Включить на этом устройстве
          </Button>
        )}
        {error ? <div className="form-error">{error}</div> : null}
      </div>
    </div>
  )
}

function InstallPanel() {
  const [prompt, setPrompt] = useState<InstallPromptEvent | null>(null)
  const isStandalone =
    matchMedia("(display-mode: standalone)").matches ||
    ("standalone" in navigator && Boolean((navigator as Navigator & { standalone?: boolean }).standalone))
  const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent)
  useEffect(() => {
    const listener = (event: Event) => {
      event.preventDefault()
      setPrompt(event as InstallPromptEvent)
    }
    window.addEventListener("beforeinstallprompt", listener)
    return () => window.removeEventListener("beforeinstallprompt", listener)
  }, [])
  const install = async () => {
    if (!prompt) return
    await prompt.prompt()
    const choice = await prompt.userChoice
    if (choice.outcome === "accepted") showToast("Приложение добавлено")
    setPrompt(null)
  }
  return (
    <div className="settings-card install-card">
      <div className="settings-card__icon is-violet">
        <Icon name="download" />
      </div>
      <div className="settings-card__content">
        <div className="settings-card__title">
          <div>
            <h2>Приложение на главном экране</h2>
            <p>Открывается как обычное приложение и быстрее запускается.</p>
          </div>
          {isStandalone ? <Badge tone="success">установлено</Badge> : null}
        </div>
        {isStandalone ? (
          <p className="settings-success">
            <Icon name="check" size={17} /> Вы уже открыли установленную версию.
          </p>
        ) : prompt ? (
          <Button onClick={() => void install()} icon="download">
            Установить приложение
          </Button>
        ) : isIos ? (
          <ol className="install-steps">
            <li>
              <span>1</span>Откройте сайт в Safari
            </li>
            <li>
              <span>2</span>Нажмите «Поделиться» <b>□↑</b>
            </li>
            <li>
              <span>3</span>Выберите «На экран Домой»
            </li>
          </ol>
        ) : (
          <p className="muted">
            Откройте меню браузера и выберите «Установить приложение» или «Добавить на главный экран».
          </p>
        )}
      </div>
    </div>
  )
}

export function SettingsPage() {
  const { user, logout } = useAuth()
  const { activeFamily } = useFamily()
  const queryClient = useQueryClient()
  const [showAllSessions, setShowAllSessions] = useState(false)
  const [theme, setTheme] = useState<ThemePreference>(
    () => (localStorage.getItem("family-coin.theme") as ThemePreference | null) ?? "system"
  )
  const sessions = useQuery({
    queryKey: ["auth", "sessions"],
    queryFn: () => api.get<Paged<Session>>("/auth/sessions")
  })
  const revoke = useMutation({
    mutationFn: (id: string) => api.delete(`/auth/sessions/${id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["auth", "sessions"] })
      showToast("Сессия завершена")
    },
    onError: (error) => showToast(toApiMessage(error, "Не удалось завершить сессию"), "danger")
  })

  useEffect(() => {
    applyTheme(theme)
    localStorage.setItem("family-coin.theme", theme)
    const media = matchMedia("(prefers-color-scheme: dark)")
    const listener = () => theme === "system" && applyTheme("system")
    media.addEventListener("change", listener)
    return () => media.removeEventListener("change", listener)
  }, [theme])

  return (
    <div className="page settings-page">
      <PageHeader
        eyebrow="Личное пространство"
        title="Настройки"
        description="Безопасность аккаунта, внешний вид и это устройство."
      />
      {user?.requiresTwoFactorSetup ? (
        <section className="setup-required" role="alert">
          <Icon name="shield" />
          <div>
            <strong>Завершите настройку системного администратора</strong>
            <p>
              Включите 2FA ниже. До этого системная админка недоступна, но вы всегда можете войти по паролю и вернуться
              сюда.
            </p>
          </div>
        </section>
      ) : null}
      <section className="profile-summary">
        <span className="profile-summary__avatar">{initials(user?.displayName)}</span>
        <div>
          <h2>{user?.displayName}</h2>
          <p>
            @{user?.login} · {activeFamily ? ROLE_LABELS[activeFamily.role] : "без семьи"}
          </p>
        </div>
        <Badge tone={user?.systemRole === "INSTANCE_ADMIN" ? "violet" : "neutral"}>
          {user?.systemRole === "INSTANCE_ADMIN" ? "instance admin" : "семейный аккаунт"}
        </Badge>
      </section>
      <div className="settings-grid">
        <div className="settings-card">
          <div className="settings-card__icon">
            <Icon name={theme === "dark" ? "moon" : "sun"} />
          </div>
          <div className="settings-card__content">
            <div className="settings-card__title">
              <div>
                <h2>Оформление</h2>
                <p>Выберите комфортную тему.</p>
              </div>
            </div>
            <Segmented
              value={theme}
              options={[
                { value: "system", label: "Как в системе" },
                { value: "light", label: "Светлая" },
                { value: "dark", label: "Тёмная" }
              ]}
              onChange={(value) => setTheme(value as "system" | "light" | "dark")}
            />
          </div>
        </div>
        <PushPanel />
        <InstallPanel />
        <TwoFactorPanel />
        <div className="settings-card settings-card--wide">
          <div className="settings-card__icon">
            <Icon name="key" />
          </div>
          <div className="settings-card__content">
            <div className="settings-card__title">
              <div>
                <h2>Активные устройства</h2>
                <p>Долгие сессии можно завершить в любой момент.</p>
              </div>
              <Badge>{sessions.data?.items.length ?? 0}</Badge>
            </div>
            {sessions.isLoading ? (
              <LoadingState rows={3} />
            ) : sessions.isError ? (
              <ErrorState message={toApiMessage(sessions.error)} retry={() => void sessions.refetch()} />
            ) : sessions.data?.items.length ? (
              <>
                <div className="session-list">
                  {(showAllSessions ? sessions.data.items : sessions.data.items.slice(0, 5)).map((session) => (
                    <article key={session.id}>
                      <span className="session-list__device">
                        {session.deviceName?.toLocaleLowerCase("ru").includes("тел") ? "📱" : "💻"}
                      </span>
                      <div>
                        <strong>
                          {session.deviceName ?? "Неизвестное устройство"}{" "}
                          {session.current ? <Badge tone="success">это устройство</Badge> : null}
                        </strong>
                        <small>
                          Активность {formatRelative(session.lastSeenAt ?? session.createdAt)} · до{" "}
                          {formatDate(session.expiresAt)}
                        </small>
                      </div>
                      {!session.current ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          loading={revoke.isPending}
                          onClick={() => revoke.mutate(session.id)}
                        >
                          Завершить
                        </Button>
                      ) : null}
                    </article>
                  ))}
                </div>
                {sessions.data.items.length > 5 ? (
                  <Button
                    className="session-list__toggle"
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowAllSessions((value) => !value)}
                  >
                    {showAllSessions ? "Свернуть список" : `Показать все устройства (${sessions.data.items.length})`}
                  </Button>
                ) : null}
              </>
            ) : (
              <EmptyState
                icon="💻"
                title="Нет активных устройств"
                text="После следующего входа устройство появится здесь."
              />
            )}
          </div>
        </div>
      </div>
      <section className="logout-panel">
        <div>
          <strong>Завершить текущую сессию</strong>
          <p>На этом устройстве понадобится снова ввести логин, пароль и 2FA.</p>
        </div>
        <Button variant="danger" icon="logout" onClick={() => void logout()}>
          Выйти
        </Button>
      </section>
    </div>
  )
}
